import React from "react";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import DocumentElement from "../DocumentView/DocumentElement";

type ColumnAlignment = "start" | "center" | "end";

interface Props {
  element: DocumentElementType;
}

const ALIGN_TO_FLEX: Record<ColumnAlignment, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
};

const normaliseAlign = (raw: unknown): ColumnAlignment => {
  if (raw === "center") return "center";
  if (raw === "end" || raw === "right") return "end";
  return "start";
};

/**
 * Columns container — fixed 2-column layout. Each child carries a
 * `column: 'left' | 'right'` field that decides which side renders
 * it. Per-column alignment (`data.leftAlign` / `data.rightAlign`)
 * controls cross-axis alignment via CSS flex. The container itself
 * has no title, no border, and no indent — it sits transparently in
 * the surrounding document width.
 */
const Columns: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    leftAlign?: ColumnAlignment | "right";
    rightAlign?: ColumnAlignment | "right";
  };
  const leftAlign = normaliseAlign(data?.leftAlign);
  const rightAlign = normaliseAlign(data?.rightAlign);
  const allChildren = element.elements || [];
  const leftChildren = allChildren.filter(
    (c) => (c as { column?: "left" | "right" }).column !== "right",
  );
  const rightChildren = allChildren.filter(
    (c) => (c as { column?: "left" | "right" }).column === "right",
  );

  return (
    <div
      className="grid w-full gap-4"
      style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
    >
      <div
        className="flex flex-col gap-3 min-w-0"
        style={{ alignItems: ALIGN_TO_FLEX[leftAlign] }}
      >
        {leftChildren.map((child) => (
          <div key={child.id} className="w-full">
            <DocumentElement element={child} />
          </div>
        ))}
      </div>
      <div
        className="flex flex-col gap-3 min-w-0"
        style={{ alignItems: ALIGN_TO_FLEX[rightAlign] }}
      >
        {rightChildren.map((child) => (
          <div key={child.id} className="w-full">
            <DocumentElement element={child} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Columns;
